/**
 * EntitySyncBadge - Small badge showing sync suggestions on entity cards
 *
 * Shows on unlinked entity cards when there are suggestions to CREATE
 * data in the other system.
 */

'use client';

import { motion } from 'framer-motion';
import { ArrowRight, Plus, Sparkles } from 'lucide-react';
import React, { useMemo } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { FilevineEntitySyncSuggestionSetSchema } from '@/services/api/generated/entitySyncService';

export interface EntitySyncBadgeProps {
  suggestionSets: FilevineEntitySyncSuggestionSetSchema[];
  entityType: 'finch' | 'filevine';
  onClick: () => void;
  className?: string;
}

export const EntitySyncBadge: React.FC<EntitySyncBadgeProps> = ({
  suggestionSets,
  entityType,
  onClick,
  className,
}) => {
  // Calculate actionable suggestions (CREATE or UPDATE, not NO_CHANGE)
  const { actionableSuggestions, hasCreate } = useMemo(() => {
    let count = 0;
    let hasCreateAction = false;

    suggestionSets.forEach((set) => {
      const actionable = (set.suggestions ?? []).filter(
        (s) => s.status === 'PENDING' && s.action_type !== 'NO_CHANGE',
      );
      count += actionable.length;
      if (actionable.some((s) => s.action_type === 'CREATE')) {
        hasCreateAction = true;
      }
    });

    return { actionableSuggestions: count, hasCreate: hasCreateAction };
  }, [suggestionSets]);

  if (actionableSuggestions === 0) return null;

  // Determine direction text based on entity type
  const targetSystem = entityType === 'finch' ? 'Filevine' : 'Finch';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className={cn(
            'flex items-center gap-1 rounded-full',
            'border border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100 px-2 py-0.5',
            'text-[10px] font-medium text-amber-700',
            'transition-all duration-150 hover:scale-105 hover:shadow-sm',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1',
            className,
          )}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {hasCreate ? (
            <Plus className="h-2.5 w-2.5" />
          ) : (
            <Sparkles className="h-2.5 w-2.5" />
          )}
          <span>{actionableSuggestions} sync</span>
          <ArrowRight className="h-2.5 w-2.5" />
        </motion.button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <div className="space-y-1">
          <p className="font-medium">
            {actionableSuggestions} Sync Suggestion
            {actionableSuggestions !== 1 ? 's' : ''}
          </p>
          <p className="text-xs text-zinc-400">
            {hasCreate
              ? `Create new ${targetSystem} record`
              : `Update ${targetSystem} data`}
          </p>
          <p className="text-xs font-medium text-amber-600">
            Click to view details
          </p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default EntitySyncBadge;
